import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Domain from "types/domain";
import GetSuggestionsResponse from "types/getAlternativesResponse";
import GetAvailableDomainsResponse from "types/getAvailableDomainsResponse";
import GetResellersResponse from "types/getResllersResponse";
import SearchResult from "types/searchResult";
import { hideWhoisModal } from "reducers/uiSlice";
import GetExpiredDomainsResponse from "types/getExpiredDomainsResponse";
import DomainDetails from "types/domainDetails";
import GetNewDomainsResponse from "types/getNewDomainsResponse";
import NewDomain from "types/newDomain";

interface DomainState {
  searchResult: SearchResult | undefined;
  searchingDomain: boolean;
  alternativeDomainExtensions: Array<SearchResult> | undefined;
  gettingAlternativeDomainExtensions: boolean;
  alternativeDomainExtensionsGottenForDomain: string | undefined;
  gettingExpiredDomains: boolean;
  expiredDomains: Array<Domain> | undefined;
  expiredDomainsTotal: number;
  expiredDomainsPage: number;
  expiredDomainsPageSize: number;
  getExpiredDomainsError: string | undefined;
  gettingAvailableDomains: boolean;
  availableDomains: Array<Domain> | undefined;
  availableDomainsTotal: number;
  availableDomainsPage: number;
  availableDomainsPageSize: number;
  getAvailableDomainsError: string | undefined;
  gettingNewDomains: boolean;
  newDomains: Array<NewDomain> | undefined;
  getNewDomainsError: string | undefined;
  resellers: Array<string> | undefined;
  resellersFailed: boolean;
  gettingWhois: boolean;
  whois: SearchResult | undefined;
  getWhoisError: string | undefined;
  domainDetails: DomainDetails | undefined;
  gettingDomainDetails: boolean;
  getDomainDetailsError: string | undefined;
}

const initialState: DomainState = {
  searchResult: undefined,
  searchingDomain: false,
  alternativeDomainExtensions: undefined,
  gettingAlternativeDomainExtensions: false,
  alternativeDomainExtensionsGottenForDomain: undefined,
  gettingExpiredDomains: false,
  expiredDomains: undefined,
  expiredDomainsTotal: 0,
  expiredDomainsPage: 1,
  expiredDomainsPageSize: 20,
  getExpiredDomainsError: undefined,
  gettingAvailableDomains: false,
  availableDomains: undefined,
  availableDomainsTotal: 0,
  gettingNewDomains: false,
  newDomains: undefined,
  getNewDomainsError: undefined,
  availableDomainsPage: 1,
  availableDomainsPageSize: 20,
  getAvailableDomainsError: undefined,
  resellers: undefined,
  resellersFailed: false,
  gettingWhois: false,
  whois: undefined,
  getWhoisError: undefined,
  domainDetails: undefined,
  gettingDomainDetails: false,
  getDomainDetailsError: undefined,
};

const domainSlice = createSlice({
  name: "domain",
  initialState,
  reducers: {
    getDomainDetails: (state, action: PayloadAction<string>) => {
      state.gettingDomainDetails = true;
      state.getDomainDetailsError = undefined;
      state.domainDetails = undefined;
    },
    getDomainDetailsSuccess: (
      state,
      action: PayloadAction<DomainDetails | undefined>,
    ) => {
      state.gettingDomainDetails = false;
      state.domainDetails = action.payload;
    },
    getDomainDetailsFailure: (state, action: PayloadAction<string>) => {
      state.gettingDomainDetails = false;
      state.getDomainDetailsError = action.payload;
    },
    getWhois: (state, action: PayloadAction<string>) => {
      state.gettingWhois = true;
      state.getWhoisError = undefined;
      if (action.payload.length === 0) {
        state.whois = undefined;
      }
    },
    getWhoisSuccess: (
      state,
      action: PayloadAction<SearchResult | undefined>,
    ) => {
      state.gettingWhois = false;
      state.whois = action.payload;
    },
    getWhoisFailure: (state, action: PayloadAction<string>) => {
      state.gettingWhois = false;
      state.getWhoisError = action.payload;
    },
    searchDomain: (state, action: PayloadAction<string>) => {
      state.searchingDomain = true;
      if (action.payload.length === 0) {
        state.searchResult = undefined;
      }
    },
    searchDomainSuccess: (
      state,
      action: PayloadAction<SearchResult | undefined>,
    ) => {
      state.searchingDomain = false;
      state.searchResult = action.payload
        ? { ...action.payload, loading: false }
        : undefined;
      state.alternativeDomainExtensionsGottenForDomain = undefined;
    },
    searchDomainFailure: (state) => {
      state.alternativeDomainExtensionsGottenForDomain = undefined;
      state.searchingDomain = false;
    },
    searchAlternativeDomainExtension: (
      state,
      action: PayloadAction<string>,
    ) => {
      if (!state.alternativeDomainExtensions) return;
      state.alternativeDomainExtensions.forEach((element) => {
        if (element.domain === action.payload) {
          element.loading = true;
          element.registered = false;
        }
      });
    },
    searchAlternativeDomainExtensionSuccess: (
      state,
      action: PayloadAction<SearchResult>,
    ) => {
      if (!state.alternativeDomainExtensions) return;
      state.alternativeDomainExtensions.forEach((element) => {
        if (element.domain === action.payload.domain) {
          element.loading = false;
          element.records = action.payload.records;
          element.registered = action.payload.registered;
        }
      });
    },
    searchAlternativeDomainExtensionFailure: (
      state,
      action: PayloadAction<string>,
    ) => {
      if (!state.alternativeDomainExtensions) return;
      state.alternativeDomainExtensions.forEach((element) => {
        if (element.domain === action.payload) {
          element.loading = false;
        }
      });
    },
    getAlternativeDomainExtensions: (state, action: PayloadAction<string>) => {
      state.gettingAlternativeDomainExtensions = true;
      state.alternativeDomainExtensionsGottenForDomain = action.payload;
    },
    getAlternativeDomainExtensionsSuccess: (
      state,
      action: PayloadAction<GetSuggestionsResponse>,
    ) => {
      state.gettingAlternativeDomainExtensions = false;
      state.alternativeDomainExtensions = action.payload.results.map(
        (r) => ({ domain: r, loading: false }) as SearchResult,
      );
    },
    getAlternativeDomainExtensionsFailure: (state) => {
      state.gettingAlternativeDomainExtensions = false;
      state.alternativeDomainExtensions = undefined;
    },
    getExpiredDomains: (
      state,
      action: PayloadAction<{ page: number; pageSize?: number }>,
    ) => {
      state.gettingExpiredDomains = true;
      state.expiredDomains = undefined;
      state.expiredDomainsPage = action.payload.page;
      if (action.payload.pageSize) {
        state.expiredDomainsPageSize = action.payload.pageSize;
      }
    },
    getExpiredDomainsSuccess: (
      state,
      action: PayloadAction<GetExpiredDomainsResponse>,
    ) => {
      state.gettingExpiredDomains = false;
      state.expiredDomains = action.payload.results;
      state.expiredDomainsTotal = action.payload.totalCount || 0;
    },
    getExpiredDomainsFailure: (state, action: PayloadAction<string>) => {
      state.gettingExpiredDomains = false;
      state.getExpiredDomainsError = action.payload;
    },
    getAvailableDomains: (
      state,
      action: PayloadAction<{
        page: number;
        pageSize?: number;
        search?: string;
      }>,
    ) => {
      state.gettingAvailableDomains = true;
      state.availableDomains = undefined;
      state.availableDomainsPage = action.payload.page;
      if (action.payload.pageSize) {
        state.availableDomainsPageSize = action.payload.pageSize;
      }
    },
    getAvailableDomainsSuccess: (
      state,
      action: PayloadAction<GetAvailableDomainsResponse>,
    ) => {
      state.gettingAvailableDomains = false;
      state.availableDomains = action.payload.results;
      state.availableDomainsTotal = action.payload.totalCount || 0;
    },
    getAvailableDomainsFailure: (state, action: PayloadAction<string>) => {
      state.gettingAvailableDomains = false;
      state.getAvailableDomainsError = action.payload;
    },
    getNewDomains: (state) => {
      state.gettingNewDomains = true;
      state.newDomains = undefined;
      state.getNewDomainsError = undefined;
    },
    getNewDomainsSuccess: (
      state,
      action: PayloadAction<GetNewDomainsResponse>,
    ) => {
      state.gettingNewDomains = false;
      state.newDomains = action.payload.domains;
    },
    getNewDomainsFailure: (state, action: PayloadAction<string>) => {
      state.gettingNewDomains = false;
      state.getNewDomainsError = action.payload;
    },
    getResellers: (state) => {
      state.resellers = undefined;
      state.resellersFailed = false;
    },
    getResellersSuccess: (
      state,
      action: PayloadAction<GetResellersResponse>,
    ) => {
      state.resellers = action.payload.resellers;
    },
    getResellersFailure: (state) => {
      state.resellersFailed = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hideWhoisModal, (state) => {
      state.whois = undefined;
    });
  },
});

export const {
  searchDomainSuccess,
  searchDomainFailure,
  getAlternativeDomainExtensionsSuccess,
  getAlternativeDomainExtensionsFailure,
  searchAlternativeDomainExtensionSuccess,
  searchAlternativeDomainExtensionFailure,
  getExpiredDomainsSuccess,
  getExpiredDomainsFailure,
  getAvailableDomainsSuccess,
  getAvailableDomainsFailure,
  getNewDomainsSuccess,
  getNewDomainsFailure,
  getResellersSuccess,
  getResellersFailure,
  getWhoisSuccess,
  getWhoisFailure,
  getDomainDetailsSuccess,
  getDomainDetailsFailure,
} = domainSlice.actions;

export default domainSlice;
